/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { determineAuthAction, handleAuthLoading, handleStableAuthState } from "./authentication-helpers";
import {
  hasAuthStateChanged as hasAuthStateChangedHelper,
  hasUserButNotChecked as hasUserButNotCheckedHelper,
  shouldCheckAuthentication as shouldCheckAuthenticationHelper,
  performAuthCheck as performAuthCheckHelper
} from "./authentication-check";
import { determineAuthStatusAsync } from "./authentication-status";

/**
 * Handles authentication check action.
 * 
 * @param {any} auth - Auth context
 * @param {Function} determineAuthStatusAsync - Auth status determiner
 * @param {Function} setIsCheckingAuth - Set checking state
 * @param {Function} addLoading - Add loading
 * @param {Function} setIsAuthenticated - Set authenticated state
 * @param {Function} setGlobalError - Set global error
 * @param {Function} removeLoading - Remove loading
 * @param {any} hasCheckedAuth - Has checked auth ref
 * @returns {void}
 */
function handleCheckAction(
  auth: any,
  determineAuthStatusAsync: (auth: any) => Promise<boolean>,
  setIsCheckingAuth: (value: boolean) => void,
  addLoading: () => void,
  setIsAuthenticated: (value: boolean) => void,
  setGlobalError: (error: any) => void,
  removeLoading: () => void,
  hasCheckedAuth: { current: boolean }
): void {
  performAuthCheckHelper(
    auth,
    determineAuthStatusAsync,
    setIsCheckingAuth,
    addLoading,
    setIsAuthenticated,
    setGlobalError,
    removeLoading,
    hasCheckedAuth
  );
}

/**
 * Processes authentication effect.
 * 
 * @param {any} auth - Auth context
 * @param {boolean} isAuthenticated - Is authenticated state
 * @param {any} hasCheckedAuth - Has checked auth ref
 * @param {Function} setIsCheckingAuth - Set checking state
 * @param {Function} addLoading - Add loading
 * @param {Function} setIsAuthenticated - Set authenticated state
 * @param {Function} setGlobalError - Set global error
 * @param {Function} removeLoading - Remove loading
 * @returns {void}
 */
export function processAuthEffect(
  auth: any,
  isAuthenticated: boolean,
  hasCheckedAuth: { current: boolean },
  setIsCheckingAuth: (value: boolean) => void,
  addLoading: () => void,
  setIsAuthenticated: (value: boolean) => void,
  setGlobalError: (error: any) => void,
  removeLoading: () => void
): void {
  const authStateChanged = hasAuthStateChangedHelper(auth.isAuthenticated, isAuthenticated);
  const userButNotChecked = hasUserButNotCheckedHelper(auth.user, hasCheckedAuth.current);
  const shouldCheck = shouldCheckAuthenticationHelper(
    hasCheckedAuth.current,
    authStateChanged,
    userButNotChecked
  );
  const action = determineAuthAction(shouldCheck, auth.isLoading);

  if (action === 'check') {
    handleCheckAction(
      auth,
      determineAuthStatusAsync,
      setIsCheckingAuth,
      addLoading,
      setIsAuthenticated,
      setGlobalError,
      removeLoading,
      hasCheckedAuth
    );
  } else if (action === 'loading') {
    handleAuthLoading(setIsCheckingAuth);
  } else {
    handleStableAuthState(
      hasCheckedAuth.current,
      auth.isAuthenticated,
      setIsAuthenticated,
      setIsCheckingAuth
    );
  }
}
